import { Badge, Bullseye, EmptyState, EmptyStateVariant } from "@patternfly/react-core";
import { ErrorCircleOIcon } from "@patternfly/react-icons";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { FormContainer } from "@rhc-shared-components/form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { type FC, forwardRef, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { getProjectsList } from "../../../api/pyxis";
import { escapePyxisSpecialChars, ProductListingTypes } from "../../../constants/product";
import { ProjectContainerTypes, ProjectTypes } from "../../../constants/project";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import type { ProjectListGraphQLResponseDataEntity } from "../../../interfaces/graphql";
import { invalidateProductQueries, productDetailQueryKey } from "../../../queries/productDetails";
import { CombinedAttachedProjectsType, getAttachedProjectsCombinedType } from "../../../utils/attachedProjectValidator";
import { GetErrorMessage } from "../../../utils/error";
import { getOrgID } from "../../../utils/fetch";
import {
	DeployedAsSetType,
	getAttachedProjectBadge,
	getAttachedProjectBadgeSet,
	getDeployedAsSet,
} from "../../../utils/productDeployedAsSet";
import { DualListSelectorWrapper } from "../shared/DualListSelectorWrapper";
import { CreateFormKeys, ExistingAttachedComponentValidator, useCreateAndAttachComponent } from "./createConstants";

interface AttachExistingComponentsModalProps {
	handleCreateModal: (showModal: boolean) => void;
	ref: any;
	hideValidatedComponents?: boolean;
}

export const AttachExistingComponents: FC<AttachExistingComponentsModalProps> = forwardRef((props, ref: any) => {
	const orgId = getOrgID();
	const { attachComponentToProduct } = useCreateAndAttachComponent(props.handleCreateModal, "existing");
	const componentCache = useRef<{
		[key: string]: any;
	}>({});
	const queryClient = useQueryClient();
	const { productDetail, certificationListState, setCertificationListState } = useProductMetadata();
	const getFilterQuery = (
		searchValue?: string,
		selectedComponents?: ProjectListGraphQLResponseDataEntity[],
	): string => {
		const selectedComponentsArray = selectedComponents || [];
		let localQuery = "";
		let commonQuery = `project_status=='active'`;
		if (searchValue) {
			commonQuery += `;name=iregex='${escapePyxisSpecialChars(searchValue)}'`;
		}
		const productTypeSet = getDeployedAsSet(selectedComponentsArray);
		const joinQueries = (queries: string[]) =>
			queries
				.filter((item) => item)
				.map((item) => `(${item})`)
				.join(",");
		switch (productDetail.type) {
			case ProductListingTypes.ContainerStack: {
				let containerProjectTypes = [ProjectTypes.containers, ProjectTypes.rhel, ProjectTypes.openstackAppContainer];
				let projectContainerTypes = [
					ProjectContainerTypes.container,
					ProjectContainerTypes.scratch,
					ProjectContainerTypes.operator,
				];
				let otherProjectTypes = [ProjectTypes.helmChart, ProjectTypes.openShiftCnf];
				if (props?.hideValidatedComponents) {
					// CONNCERT-2866: Remove validated components from 'Existing Components' pick list on 'Validations & Certifications' tab
					commonQuery += `;certification_level!='Vendor validated'`;
				}
				// Remove rhel and openstackAppContainer if helm or operator is attached
				if (productTypeSet.has(DeployedAsSetType.helmChart) || productTypeSet.has(DeployedAsSetType.operator)) {
					containerProjectTypes = containerProjectTypes.filter(
						(item) => !(item === ProjectTypes.rhel || item === ProjectTypes.openstackAppContainer),
					);
				}
				// Remove helm and operator if openstackAppContainer or rhel is attached
				if (productTypeSet.has(DeployedAsSetType.openstackAppContainer) || productTypeSet.has(DeployedAsSetType.rhel)) {
					otherProjectTypes = otherProjectTypes.filter((item) => !(item === ProjectTypes.helmChart));
					projectContainerTypes = projectContainerTypes.filter((item) => !(item === ProjectContainerTypes.operator));
				}
				// Always attach compatible badged projects if a badged project is already attached
				const existingAttachedBadge = getAttachedProjectBadge(selectedComponentsArray);
				const cniCsiQuery = existingAttachedBadge
					? `;(badges=in=(null, "${existingAttachedBadge}"), badges=size=0)`
					: "";

				// If a product has cni or csi attached, remove openShiftCnf
				const existingAttachedBadgeSet = getAttachedProjectBadgeSet(selectedComponentsArray);
				if (existingAttachedBadgeSet.has("cni") || existingAttachedBadgeSet.has("csi")) {
					otherProjectTypes = otherProjectTypes.filter((item) => !(item === ProjectTypes.openShiftCnf));
				}
				const containerProjectsQuery = `${commonQuery};type=in=("${containerProjectTypes.join('","')}");container.type=in=("${projectContainerTypes.join('","')}")${cniCsiQuery}`;
				const otherTypeQuery = `${commonQuery};type=in=("${otherProjectTypes.join('","')}")`;
				localQuery = joinQueries([containerProjectsQuery, otherTypeQuery]);
				break;
			}
			case ProductListingTypes.TraditionalApplication: {
				const rhelQuery = `${commonQuery};type=='${ProjectTypes.rhel}';container==null`;
				localQuery = joinQueries([rhelQuery]);
				break;
			}
			case ProductListingTypes.OpenStackInfrastructure: {
				const projectTypes = [
					ProjectTypes.openstackVNF,
					ProjectTypes.openStackInfraContainer,
					ProjectTypes.openStackInfraNonContainer,
				];
				localQuery = `${commonQuery};type=in=("${projectTypes.join('","')}")`;
				break;
			}
			default:
				break;
		}
		return localQuery;
	};
	const [query, setQuery] = useState(getFilterQuery());
	const debouncedSetQuery = useDebouncedCallback((value) => {
		setQuery(value);
	}, 700);
	const { setAlertState } = useAlertContext();
	const { error: apiError } = useQuery({
		queryKey: ["attachableComponents", query],
		queryFn: async ({ signal }) => {
			return getProjectsList(
				orgId,
				{
					page: 0,
					page_size: 250,
				},
				{
					sort_by: ["last_update_date[desc]"],
					filter: query ? [query] : [getFilterQuery()],
				},
				signal,
			);
		},
	});

	useEffect(() => {
		if (apiError) {
			ErrorPopup(setAlertState, GetErrorMessage(apiError));
		}
	}, [apiError, setAlertState]);

	const searchHandler = (value: string, executeImmediately?: boolean) => {
		const filterQuery = getFilterQuery(value);
		debouncedSetQuery.cancel();
		if (executeImmediately) {
			setQuery(filterQuery);
		} else {
			debouncedSetQuery(filterQuery);
		}
	};
	if (apiError) {
		const errorMessage = GetErrorMessage(apiError);
		return (
			<Bullseye>
				<EmptyState
					headingLevel="h5"
					icon={ErrorCircleOIcon}
					titleText={errorMessage}
					variant={EmptyStateVariant.full}
				></EmptyState>
			</Bullseye>
		);
	}

	return (
		<Formik
			innerRef={ref}
			initialValues={{
				[CreateFormKeys.certProjects]:
					certificationListState?.map((item) => {
						const isArchived = item?.project_status === "archived";
						return {
							text: `${item?.name} - ${item?._id}`,
							node: isArchived ? (
								<span key={item?._id}>
									<Badge isRead>Archived</Badge> {item?.name} - {item?._id}
								</span>
							) : undefined,
							selected: false,
							isVisible: true,
							isDisabled: isArchived,
						};
					}) || [],
			}}
			validationSchema={ExistingAttachedComponentValidator}
			onSubmit={async (values) => {
				const attachedCertComponents = values[CreateFormKeys.certProjects].map((item) =>
					item.text.substring(item.text.lastIndexOf("-") + 1).trim(),
				);
				const newAttachedCertComponentsData = attachedCertComponents.map((id) => {
					// Check for attached components in certificationListState as well if not found in componentCache
					const cacheItem = componentCache.current?.[id];
					if (cacheItem) {
						return cacheItem;
					}
					return certificationListState?.find((certItem) => certItem?._id === id);
				});
				const combinedAttachedType = getAttachedProjectsCombinedType(newAttachedCertComponentsData);
				const projectTypeSet = getDeployedAsSet(newAttachedCertComponentsData);
				const currentBadge = getAttachedProjectBadge(newAttachedCertComponentsData);
				// https://issues.redhat.com/browse/CONNCERT-2398
				// User cannot view or select 'OpenShift Virtualization' from the 'Add Component' modal in the following cases:
				// - if a 'OpenStack-app-container' component is already attached to the product listing
				// - if a 'OpenShift-Cnf' component is already attached to the product listing
				// - If an OpenShift operator component with a badge of 'cni' or 'csi' is already attached to the product listing
				// - If another OpenShift Virtualization component is already attached to the product listing
				if (combinedAttachedType === CombinedAttachedProjectsType.openShiftVirtualization) {
					if (
						projectTypeSet.has(DeployedAsSetType.openstackAppContainer) ||
						projectTypeSet.has(DeployedAsSetType.openShiftCnf)
					) {
						ErrorPopup(
							setAlertState,
							"You cannot attach a OpenStack app container or OpenShift CNF with a Openshift virtualization component",
						);
						return false;
					}
					if (currentBadge && ["cni", "csi"].includes(currentBadge)) {
						ErrorPopup(
							setAlertState,
							"You cannot attach a CNI or CSI Operator with a Openshift virtualization component",
						);
						return false;
					}
					if (
						newAttachedCertComponentsData.filter((item) => item?.type === ProjectTypes.openShiftVirtualization)
							?.length !== 1
					) {
						ErrorPopup(
							setAlertState,
							"You cannot attach more than one Openshift virtualization component to a product",
						);
						return false;
					}
				}
				const payload: any = {
					_id: productDetail._id,
					[CreateFormKeys.certProjects]: attachedCertComponents,
				};
				await attachComponentToProduct.mutateAsync(payload);
				setCertificationListState(newAttachedCertComponentsData);
				SuccessPopup(setAlertState, `Component${attachedCertComponents?.length > 1 ? "s" : ""} attached successfully`);
				await queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productDetail._id) });
				invalidateProductQueries(queryClient, productDetail._id);
				props.handleCreateModal(false);
				document.body.classList.remove("stop-scrolling");
			}}
		>
			<FormContainer gridSpan={12}>
				<DualListSelectorWrapper
					searchHandler={searchHandler}
					getFilterQuery={getFilterQuery}
					componentCache={componentCache}
					debouncedSetQuery={debouncedSetQuery}
					query={query}
				/>
			</FormContainer>
		</Formik>
	);
});
